import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Crown, TrendingDown, Users, Star, AlertTriangle, Check, Sparkles,
  ArrowUpCircle, ArrowDownCircle } from "lucide-react";

const REL = { high: { it: "Alta", en: "High", c: "#16a34a" }, medium: { it: "Media", en: "Medium", c: "#d97706" }, low: { it: "Bassa", en: "Low", c: "#ef4444" } };

function Row({ r, lang, onSave }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [pct, setPct] = useState(r.calibration_pct ?? "");
  const [saving, setSaving] = useState(false);
  const rel = REL[r.reliability?.level] || REL.low;
  const save = async () => {
    setSaving(true);
    try { await onSave(r.competitor_id, pct === "" ? null : Number(pct), r.public_price); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-lg border border-border p-3" data-testid={`tier-row-${r.tier}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold truncate">{r.name || "—"}</span>
            {r.stars ? <span className="text-xs text-amber-500 flex items-center">{Array.from({ length: r.stars }).map((_, i) => <Star key={i} className="w-3 h-3 fill-amber-400" />)}</span> : null}
            {r.similarity != null && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted">sim {Math.round(r.similarity)}/100</span>}
          </div>
          <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
            <span>{L("Prezzo Booking", "Booking price")}: <b>{r.public_price != null ? `€${r.public_price}` : "—"}</b></span>
            <span>{L("Stimato reale", "Estimated")}: <b>{r.estimated_price != null ? `€${r.estimated_price}` : "—"}</b></span>
            <span>{L("Affidabilità", "Reliability")}: <b style={{ color: rel.c }}>{lang === "en" ? rel.en : rel.it}{r.reliability?.days != null ? ` · ${r.reliability.days}gg` : ""}</b></span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">{L("Corr.", "Corr.")}</span>
            <input type="number" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="0"
              data-testid={`tier-pct-${r.competitor_id?.slice(-12)}`}
              className="w-16 h-8 rounded border border-border bg-background px-2 text-sm text-right" />
            <span className="text-xs text-muted-foreground">%</span>
          </div>
          <button onClick={() => setPct(String(r.ai_suggestion?.pct ?? 0))} title={`AI: ${r.ai_suggestion?.pct}% (${r.ai_suggestion?.reason})`}
            data-testid="tier-ai-suggest" className="h-8 px-2 rounded border border-border text-xs flex items-center gap-1 hover:bg-muted">
            <Sparkles className="w-3.5 h-3.5 text-pink-500" />{r.ai_suggestion?.pct}%
          </button>
          <button onClick={save} disabled={saving} data-testid="tier-save"
            className="h-8 px-3 rounded bg-primary text-primary-foreground text-xs font-medium flex items-center gap-1 disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}{L("Verifica", "Verify")}
          </button>
        </div>
      </div>
      {(r.alerts || []).map((a, i) => (
        <div key={i} className="mt-2 text-xs flex items-start gap-1.5 text-amber-700 bg-amber-500/10 rounded px-2 py-1" data-testid="tier-alert">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-500" />{a}
        </div>
      ))}
    </div>
  );
}

function Section({ icon: Icon, title, subtitle, rows, lang, onSave, empty, accent, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-5 h-5" style={{ color: accent }} />
        <h3 className="font-semibold">{title}</h3>
        <span className="text-xs text-muted-foreground">({rows.length})</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">{subtitle}</p>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground">{empty}</p> : (
        <div className="space-y-2">{rows.map((r) => <Row key={r.competitor_id} r={r} lang={lang} onSave={onSave} />)}</div>
      )}
    </div>
  );
}

export default function CompetitorTiers({ propId, lang, refreshKey }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (refresh) => {
    setLoading(true);
    try { const { data } = await api.get(`/properties/${propId}/competitor-tiers?lang=${lang}${refresh ? "&refresh=true" : ""}`); setD(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [propId, lang]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const onSave = async (competitor_id, pct, public_price) => {
    try {
      await api.put(`/properties/${propId}/competitor-calibration`, { competitor_id, pct, public_price });
      toast.success(L("Calibrazione salvata", "Calibration saved"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading && !d) return <div className="md:col-span-4 bg-card border border-border rounded-lg p-6"><Loader2 className="w-5 h-5 animate-spin inline text-muted-foreground" /></div>;
  if (!d) return null;
  const b = d.bounds || {};

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="md:col-span-4 space-y-4" data-testid="competitor-tiers">
      {/* Bounds banner */}
      <div className="rounded-xl border border-border bg-gradient-to-r from-primary/5 to-transparent p-4 flex flex-wrap items-center gap-x-8 gap-y-2" data-testid="tiers-bounds">
        <div className="flex items-center gap-2"><ArrowUpCircle className="w-5 h-5 text-purple-600" /><span className="text-sm">{L("Tetto mercato (Premium)", "Market ceiling (Premium)")}: <b className="text-lg">{b.premium_ceiling != null ? `€${b.premium_ceiling}` : "—"}</b></span></div>
        <div className="flex items-center gap-2"><ArrowDownCircle className="w-5 h-5 text-emerald-600" /><span className="text-sm">{L("Pavimento mercato (Economici)", "Market floor (Budget)")}: <b className="text-lg">{b.budget_floor != null ? `€${b.budget_floor}` : "—"}</b></span></div>
        <p className="text-xs text-muted-foreground flex-1 min-w-[240px]">{L("Premium ed Economici NON entrano nel calcolo: fanno solo da limite. Con confidenza molto alta il motore può superarli se gli altri segnali sono forti.", "Premium & Budget don't enter the calculation: they only act as bounds. With very high confidence the engine may exceed them when other signals are strong.")}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Section icon={Users} accent="#2563eb" testid="tiers-direct"
          title={L("Competitor Diretti", "Direct competitors")}
          subtitle={L("Entrano nel calcolo del prezzo. Bloccati finché non li cambi tu.", "They drive the price. Locked until you change them.")}
          rows={d.direct} lang={lang} onSave={onSave}
          empty={L("Nessun competitor diretto selezionato.", "No direct competitor selected.")} />
        <Section icon={Crown} accent="#7c3aed" testid="tiers-premium"
          title={L("Competitor Premium", "Premium competitors")}
          subtitle={L("Solo limite superiore. Aggiornati automaticamente dal mercato.", "Upper bound only. Auto-updated from the market.")}
          rows={d.premium} lang={lang} onSave={onSave}
          empty={L("Nessun candidato premium trovato nella micro-zona.", "No premium candidate found in the micro-zone.")} />
        <Section icon={TrendingDown} accent="#16a34a" testid="tiers-budget"
          title={L("Competitor Economici", "Budget competitors")}
          subtitle={L("Solo limite inferiore. Aggiornati automaticamente dal mercato.", "Lower bound only. Auto-updated from the market.")}
          rows={d.budget} lang={lang} onSave={onSave}
          empty={L("Nessun candidato economico trovato nella micro-zona.", "No budget candidate found in the micro-zone.")} />
      </div>
    </motion.div>
  );
}
